import { emptyRule, helpers, innRule, maxLengthRule, requiredRule } from '~/shared/utils/validation'
import { isUnknownObject } from '~/shared/utils/guards'
import { fillOwnProperties } from '~/shared/utils/helpers'
import { Form } from '~/shared/api'
import { VideoModel } from '~/model/Video'
import { QuoteModel, TextNumberModel, EmployerModel, TextTaskModel, WarrantyModel } from '~/model/Employer'
import { RegionModel } from '~/model/Region'
import { GeoPositionModel } from '~/model/Geo'

export default class AccountEmployerPageForm extends Form {
  id: number = 0
  name: string = ''
  slug: string = ''
  inn: string = ''
  description: string = ''
  companyDescription: string = ''
  link: string = ''
  logo: File[] = []
  image: File[] = []
  regions: Array<RegionModel> = []
  video: Array<VideoModel> = []
  geoPositions: Array<GeoPositionModel> = []
  sliderImages: File[] = []
  tasks: Array<TextTaskModel> = []
  numbers: Array<TextNumberModel> = []
  quotes: Array<QuoteModel> = []
  warranties: Array<WarrantyModel> = []

  constructor(employer?: EmployerModel) {
    super()
    this.fillForm(employer)
    this.initialize()
  }

  private initialize() {
    if (this.numbers.length === 0) {
      this.numbers = Array(3)
        .fill(null)
        .map(() => new TextNumberModel(null))
    }

    if (this.quotes.length === 0) {
      this.quotes = Array(1)
        .fill(null)
        .map(() => new QuoteModel(null))
    }

    if (this.geoPositions.length === 0) {
      this.geoPositions = Array(1)
        .fill(null)
        .map(() => new GeoPositionModel(null))
    }

    if (this.video.length === 0) {
      this.video = Array(1)
        .fill(null)
        .map(() => new VideoModel(null))
    }

    if (this.tasks.length === 0) {
      this.tasks.push(new TextTaskModel(null))
    }

    if (this.warranties.length === 0) {
      this.warranties.push(new WarrantyModel(null))
    }
  }

  fillForm(employer?: EmployerModel) {
    if (isUnknownObject(employer)) {
      fillOwnProperties(this, employer)
    }
  }

  addEmptyNumber() {
    this.numbers.push(new TextNumberModel(null))
  }

  deleteNumber(number: TextNumberModel) {
    const idx = this.numbers.findIndex(i => i.number === number.number)
    if (idx >= 0) {
      this.numbers.splice(idx, 1)
    }
  }

  addEmptyQuote() {
    this.quotes.push(new QuoteModel(null))
  }

  deleteQuote(quote: QuoteModel) {
    const idx = this.quotes.findIndex(i => i.author === quote.author)
    if (idx >= 0) {
      this.quotes.splice(idx, 1)
    }
  }

  addEmptyVideo() {
    this.video.push(new VideoModel(null))
  }

  deleteVideo(video: VideoModel) {
    const idx = this.video.findIndex(i => i.link === video.link)
    if (idx >= 0) {
      this.video.splice(idx, 1)
    }
  }

  deleteGeo(geo: GeoPositionModel) {
    const idx = this.geoPositions.findIndex(i => i.name === geo.name)
    if (idx >= 0) {
      this.geoPositions.splice(idx, 1)
    }
  }

  addEmptyGeo() {
    this.geoPositions.push(new GeoPositionModel(null))
  }

  addEmptyDirection() {
    this.tasks.push(new TextTaskModel(null))
  }

  deleteDirection(direction: TextTaskModel) {
    const idx = this.tasks.findIndex(i => i.text === direction.text)
    if (idx >= 0) {
      this.tasks.splice(idx, 1)
    }
  }

  addEmptyWarranty() {
    this.warranties.push(new WarrantyModel(null))
  }

  deleteWarranty(warranty: WarrantyModel) {
    const idx = this.warranties.findIndex(i => i.text === warranty.text)
    if (idx >= 0) {
      this.warranties.splice(idx, 1)
    }
  }

  static bannerRules() {
    return {
      name: requiredRule,
      inn: { ...requiredRule, ...innRule },
      logo: requiredRule,
      image: requiredRule,
      companyDescription: emptyRule
    }
  }

  static aboutCompanyRules() {
    return {
      link: requiredRule,
      description: requiredRule,
      numbers: {
        $each: helpers.forEach({
          number: requiredRule,
          text: requiredRule
        })
      }
    }
  }

  static directionsRules() {
    return {
      tasks: {
        $each: helpers.forEach({
          text: { ...requiredRule, ...maxLengthRule(255) }
        })
      },
      sliderImages: requiredRule
    }
  }

  static warrantiesRules() {
    return {
      warranties: emptyRule
    }
  }

  static quotesRules() {
    return {
      quotes: {
        $each: helpers.forEach({
          text: requiredRule
        })
      }
    }
  }

  static geoRules() {
    return {
      geoPositions: {
        $each: helpers.forEach({
          name: requiredRule,
          text: requiredRule
        })
      }
    }
  }

  static videosRules() {
    return {
      video: {
        $each: helpers.forEach({
          link: requiredRule,
          name: requiredRule
        })
      }
    }
  }
}
